import { Fragment, useEffect, useMemo, useState } from "react";

import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { CrmJobsTransitionBanner } from "app/pages/sales-intelligence/modules/industry/components/CrmJobsTransitionBanner/CrmJobsTransitionBanner";

import ManagementCardPlaceholder from "../components/cards-components/ManagementCardPlaceholder";
import CardsWrapper from "../components/CardsWrapper/CardsWrapper";
import CardTypeFilterDropdown from "../components/CardTypeFilterDropdown/CardTypeFilterDropdown";
import MyIntegrationsEmptyState from "../components/MyIntegrationsEmptyState/MyIntegrationsEmptyState";
import type { CardType } from "../constants/cardTypes";
import { useMyIntegrationCards } from "../hooks/useMyIntegrationCards";
import { filterCardsByType, getAvailableCardTypes } from "../utils/cardFiltering";

const MyIntegrationsSectionContainer = (): React.ReactElement | null => {
    const [selectedType, setSelectedType] = useState<CardType | null>(null);
    const { cards, isLoading } = useMyIntegrationCards();

    const availableTypes = useMemo(() => getAvailableCardTypes(cards), [cards]);

    useEffect(() => {
        if (selectedType !== null && !availableTypes.includes(selectedType)) {
            setSelectedType(null);
        }
    }, [availableTypes, selectedType]);

    if (isLoading) {
        return (
            <CardsWrapper>
                <ManagementCardPlaceholder />
                <ManagementCardPlaceholder />
                <ManagementCardPlaceholder />
                <ManagementCardPlaceholder />
            </CardsWrapper>
        );
    }

    if (cards.length === 0) {
        return <MyIntegrationsEmptyState />;
    }

    const filteredCards = filterCardsByType(cards, selectedType);

    return (
        <Stack
            gap="32px"
            data-automation="my-integrations-section"
        >
            <Flex
                justifyContent="space-between"
                alignItems="center"
            >
                <CardTypeFilterDropdown
                    availableTypes={availableTypes}
                    selectedType={selectedType}
                    onChange={setSelectedType}
                />
            </Flex>

            <CrmJobsTransitionBanner />

            <CardsWrapper>
                {filteredCards.map((card) => (
                    <Fragment key={card.id}>{card.node}</Fragment>
                ))}
            </CardsWrapper>
        </Stack>
    );
};

export default MyIntegrationsSectionContainer;
